// Backend API service for Ristocat game
import { Level, LevelData } from "../data/LevelModel";

// Backend API response types
export interface BackendLevelResponse {
  budget: number;
  configuration: {
    categories: Array<{
      budgeted: number;
      name: string;
      necessary: boolean;
      spent: number;
      savings: boolean;
    }>;
  };
  difficulty: number;
  level: number;
  income: number;
  nickname: string;
  precision: number;
  savings: number;
  steps: number;
  surprise_amount: number;
  surprise_events: number;
  tokens: number;
  // New backend fields (June 2025)
  id: number; // Unique level row ID in DB
}

// Request payload for POST requests
export interface LevelRequest {
  uid: string;
  // Add other parameters as needed
  currentLevelId?: number;
  difficulty?: number;
}

// Base backend service class
export class BackendService {
  private static instance: BackendService;
  private readonly baseUrl: string;

  // Singleton pattern
  public static getInstance(): BackendService {
    if (!BackendService.instance) {
      BackendService.instance = new BackendService();
    }
    return BackendService.instance;
  }

  private constructor() {
    // Determine backend URL based on current environment
    this.baseUrl = this.getBackendUrl();
    console.log("Backend service initialized with URL:", this.baseUrl);
  }

  /**
   * Determine the correct backend URL based on the current environment
   */
  private getBackendUrl(): string {
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;

      // Check if we're running on localhost
      if (
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname === "192.168.4.33"
      ) {
        return "http://localhost:8080"; // 'http://192.168.4.33:8080';
      }
    }

    // Default to production URL
    return "https://us-central1-ristocat-game.cloudfunctions.net/api";
  }

  /**
   * Generic method to make POST requests to the backend
   */
  private async makePostRequest<T>(endpoint: string, payload: any): Promise<T> {
    try {
      console.log(`Making request to ${this.baseUrl}${endpoint}`, payload);

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Backend request failed: ${response.status} ${response.statusText} - ${errorText}`,
        );
      }

      const data = await response.json();
      console.log(`Response from ${endpoint}:`, data);
      return data as T;
    } catch (error) {
      console.error(`Error making request to ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Fetch the next level for a user
   */
  async fetchNextLevel(uid: string, currentLevelId?: number): Promise<Level> {
    const payload: LevelRequest = {
      uid,
      currentLevelId,
    };

    const backendLevel = await this.makePostRequest<BackendLevelResponse>(
      "/level/next",
      payload,
    );

    // Transform backend response to our Level model
    return this.transformBackendLevel(backendLevel);
  }

  /**
   * Fetch a specific level by ID
   */
  async fetchLevelByName(uid: string, levelName: string): Promise<Level> {
    const payload = {
      uid,
      name: levelName,
    };

    const backendLevel = await this.makePostRequest<BackendLevelResponse>(
      "/level/get",
      payload,
    );
    return this.transformBackendLevel(backendLevel);
  }

  /**
   * Transform backend level data to our internal Level model
   */
  private transformBackendLevel(backendLevel: BackendLevelResponse): Level {
    // Map backend categories to our category format
    const categories = backendLevel.configuration.categories.map(
      (category) => ({
        group: category.name, // Backend uses 'name', we use 'group' for categorization
        name: category.name,
        necessary: category.necessary,
        budgeted: category.budgeted,
        spent: category.spent,
        savings: category.savings,
      }),
    );

    // Choose user-visible level number: fall back to old field if new one missing
    const userLevel = backendLevel.level;

    // Transform to our LevelData interface
    const levelData: LevelData = {
      id: backendLevel.id,
      level: userLevel,
      input_level: userLevel, // keep legacy alias used across UI
      nickname: backendLevel.nickname,
      active: true, // New levels are active by default
      reviewed: false, // New levels haven't been reviewed yet
      categories,
      tokens: backendLevel.tokens,
      weighted_dials: backendLevel.difficulty, // Map difficulty to weighted_dials
      dial_precision: backendLevel.precision,
      dial_visible_categories: categories.length,
      dial_unbalanced_categories: categories.filter(
        (cat) => cat.budgeted !== cat.spent,
      ).length,
      dial_surprise_events: backendLevel.surprise_events,
      dial_steps: backendLevel.steps,
      dial_budget: backendLevel.budget,
      dial_income: backendLevel.income,
      dial_savings: backendLevel.savings,
      dial_surprise_amount: backendLevel.surprise_amount,
    };

    return new Level(levelData);
  }

  /**
   * Save a user's attempt at a level
   */
  async saveAttempt(
    attempt: any,
  ): Promise<{ tokens: number; mistakes: string[] }> {
    try {
      console.log("Starting saveAttempt with attempt object:", attempt);
      console.log("Attempt type:", typeof attempt);
      console.log(
        "Attempt has toJSON method:",
        typeof attempt.toJSON === "function",
      );

      const attemptData = attempt.toJSON();
      console.log("toJSON() returned:", attemptData);

      const response = await this.makePostRequest<{
        tokens: number;
        mistakes: string[];
      }>("/level/save", attemptData);
      console.log("Successfully saved attempt, received response:", response);
      return response;
    } catch (error) {
      console.error("Failed to save attempt:", error);
      throw error;
    }
  }

  /**
   * Submit level completion results
   */
  async submitLevelResult(
    uid: string,
    levelId: string,
    success: boolean,
    score?: number,
  ): Promise<void> {
    const payload = {
      uid,
      levelId,
      success,
      score,
      timestamp: new Date().toISOString(),
    };

    try {
      await this.makePostRequest("/submit_result", payload);
      console.log("Successfully submitted level result");
    } catch (error) {
      console.error("Failed to submit level result:", error);
      throw error;
    }
  }

  /**
   * Fetch user progress/stats (placeholder for future use)
   */
  async fetchUserProgress(uid: string): Promise<any> {
    const payload = { uid };

    try {
      return await this.makePostRequest("/user_progress", payload);
    } catch (error) {
      console.log("User progress endpoint not available yet:", error);
      return null;
    }
  }

  /**
   * Inform backend that a new auth user has been created so it can
   * initialise DB rows etc. Safe to call multiple times – backend
   * should treat it as idempotent.
   */
  async createUser(uid: string, username: string): Promise<void> {
    try {
      console.log(
        `BackendService.createUser called with uid: ${uid}, username: ${username}`,
      );
      await this.makePostRequest("/user/create", { uid, username });
      console.log(
        `BackendService.createUser completed successfully for ${uid}`,
      );
    } catch (error) {
      // If user already exists the backend might return 409 or similar.
      // Swallow non-fatal errors so login flow continues uninterrupted.
      console.error("createUser failed (ignored):", error);
    }
  }

  /**
   * Fetch user's level statistics
   */
  async fetchLevelStats(uid: string): Promise<any> {
    const payload = { uid };
    try {
      return await this.makePostRequest("/user/stats", payload);
    } catch (error) {
      console.log("Level stats endpoint not available yet:", error);
      return null;
    }
  }
}

// Export singleton instance for easy importing
export const backendService = BackendService.getInstance();
